import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
} from '@angular/core';

import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';

import { AbilitiesSelectors } from '@recruitee/abilities-data';
import { Ability } from '@recruitee/abilities-types';
import { CurrentUserSelectors } from '@recruitee/account-data';
import type { CurrentUser } from '@recruitee/account-types';
import { QuickEvaluationContext } from '@recruitee/analytics';
import { filterBoolean, trackByProp, trackByValue } from '@recruitee/common';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import type { InterviewResultRequest } from '@recruitee/interview-result-request-types';
import { InterviewGroupBy } from '@recruitee/interview-results-types';
import type {
  HiddenResultPreview,
  InterviewResult,
  RatingLevelTranslations,
} from '@recruitee/interview-results-types';
import {
  InterviewTemplateScope,
  InterviewTemplateSelectors,
} from '@recruitee/interview-templates-data';
import type { InterviewTemplate } from '@recruitee/interview-templates-types';
import type { OfferBrief } from '@recruitee/offers-types';
import { OfferSelectOptionHelper, PlacementOfferSelectOption } from '@recruitee/offers-ui';
import type { Placement } from '@recruitee/placements-types';
import type { Stage } from '@recruitee/stages-types';
import { HiddenEvaluationsInfoboxComponent } from '@recruitee/unbiased-hiring';
import {
  DialogRef,
  RtStatefulComponent,
  SelectGroupedOptions,
  SelectGroupOption,
  InterviewEvaluationRatingComponent,
  DividerComponent,
  IconComponent,
  IconColorDirective,
  HeadingComponent,
} from '@recruitee/user-interface';
import { sortByKey } from '@recruitee/utils';

import { InterviewResultCardComponent } from './interview-result-card/interview-result-card.component';
import { InterviewResultRequestCardComponent } from './interview-result-request-card/interview-result-request-card.component';

type State = {
  expanded: boolean;
  currentAdmin: CurrentUser | null;
  offers: PlacementOfferSelectOption[];
  templates: SelectGroupedOptions[];
  hasPlacementFairEvalEnabled: Map<number, boolean>;
};

@Component({
  selector: 'rt-interview-list',
  templateUrl: 'interview-list.component.html',
  styleUrls: ['./interview-list.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    HeadingComponent,
    IconColorDirective,
    IconComponent,
    DividerComponent,
    InterviewEvaluationRatingComponent,
    HiddenEvaluationsInfoboxComponent,
    InterviewResultRequestCardComponent,
    InterviewResultCardComponent,
    TranslatePipe,
  ],
})
export class InterviewListComponent extends RtStatefulComponent<State> {
  @Input() public dialogRef: DialogRef;
  @Input()
  public items: Array<InterviewResult | InterviewResultRequest> = [];
  @Input() public hiddenResultsPreview?: HiddenResultPreview[] = [];
  @Input() public offer?: OfferBrief | null;
  @Input() public type: 'request' | 'result' | 'rating';
  @Input() public title: string = '';
  @Input() public rating: null | undefined | number;
  @Input() public groupBy?: InterviewGroupBy;
  @Input() public edit: boolean = true;
  @Input() public short: boolean = false;
  @Input() public hasTitle: boolean = true;
  @Input() public inCandidate: boolean = false;
  @Input() public openCandidateOnClick: boolean = false;
  @Input() public openEventModalOnClick: boolean = false;
  @Input() public inDashboard: boolean = false;
  @Input() public hasHeader: boolean = false;
  @Input() public hasMoreThanOneJob: boolean = false;
  @Input() public candidatePlacements?: Placement[] = [];
  @Input() public currentUserAvailablePlacements: Placement[] = [];
  @Input() public hasAccessToAllJobOffers?: boolean;
  @Input() public candidateId?: number;
  @Input() public ratingLevelTranslations?: RatingLevelTranslations;

  @Output() public quickEvaluationClick: EventEmitter<{
    offerId: number;
    context: QuickEvaluationContext;
  }> = new EventEmitter<{ offerId: number; context: QuickEvaluationContext }>();

  public abilities = {
    viewCandidates: false,
    highLevelDataAccess: false,
    reviewHiddenEvaluations: false,
    manageCandidates: false,
  };
  public trackById = trackByProp<{ id: number }>({ prop: 'id' });
  public trackByValue = trackByValue;
  private ngOnDestroy$ = new Subject<void>();

  constructor(
    protected cdRef: ChangeDetectorRef,
    private store: Store,
    public i18n: I18nService,
  ) {
    super(cdRef);
    this.createState({
      expanded: true,
      currentAdmin: null,
      templates: [],
      offers: [],
      hasPlacementFairEvalEnabled: new Map<number, boolean>(),
    });
  }

  public ngOnInit(): void {
    this.store
      .select(CurrentUserSelectors.data)
      .pipe(first(), takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(currentAdmin => {
        this.setState({
          ...this.state,
          currentAdmin,
        });
      });
    this.checkAbilities();
    this.getData();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.candidatePlacements || changes.currentUserAvailablePlacements) {
      this.setState({
        ...this.state,
        offers: OfferSelectOptionHelper.placementsToOfferSelectOptions(
          this.currentUserAvailablePlacements,
        ),
        hasPlacementFairEvalEnabled: this.reduceCandidatePlacementsToFairEvalStatus(),
      });
    }

    if (changes.items && this.candidatePlacements?.length) {
      this.setState({
        ...this.state,
        hasPlacementFairEvalEnabled: this.reduceCandidatePlacementsToFairEvalStatus(),
      });
    }
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public hasPlacementFairEvalEnabled(offerId: number | null): boolean {
    if (offerId) {
      return this.state.hasPlacementFairEvalEnabled.get(offerId) === true;
    }

    return false;
  }

  public toggle(): void {
    this.setState({
      ...this.state,
      expanded: !this.state.expanded,
    });
  }

  public hasRating(): boolean {
    return this.rating !== null || !!this.hiddenResultsPreview?.length;
  }

  public hasAccessToJob(offerId: number): boolean {
    return (
      this.hasAccessToAllJobOffers ||
      this.currentUserAvailablePlacements.some(placement => placement.offerId === offerId)
    );
  }

  public get hiddenEvaluationsJobNameVisible(): boolean {
    if (this.hasMoreThanOneJob) {
      return this.groupBy !== 'stage' && this.hiddenResultsPreview!.length > 1;
    } else {
      return !this.groupBy;
    }
  }

  public isCurrentStage(offer: OfferBrief, stage: Stage): boolean {
    return (
      stage.id ===
      this.candidatePlacements?.find(placement => placement.offerId === offer.id)?.stageId
    );
  }

  private reduceCandidatePlacementsToFairEvalStatus(): Map<number, boolean> {
    return (
      this.candidatePlacements?.reduce(
        (accumulator, placement) =>
          accumulator.set(placement.offerId, !!placement.stage?.fairEvaluationsEnabled),
        new Map(),
      ) ?? new Map()
    );
  }

  private mapTemplates(templates: InterviewTemplate[]): SelectGroupedOptions[] {
    const groups = templates
      .sort(
        sortByKey('category', {
          direction: 'desc',
          modifyFn: (category: string) => category && category.toLowerCase(),
        }),
      )
      .reduce<Record<string, SelectGroupOption[]>>((acc, template) => {
        const category = template.category || this.i18n.translate('app.common.other');
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push({ label: template.name, value: template.id! });
        return acc;
      }, {});
    return Object.entries(groups).map(([title, options]) => ({ title, options }));
  }

  private checkAbilities(): void {
    this.store
      .pipe(select(AbilitiesSelectors.abilityMap), takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(enabledMap => {
        this.abilities = {
          highLevelDataAccess: enabledMap[Ability.HighLevelDataAccess],
          viewCandidates: enabledMap[Ability.ViewCandidates],
          reviewHiddenEvaluations: enabledMap[Ability.ReviewHiddenEvaluations],
          manageCandidates: enabledMap[Ability.ManageCandidates],
        };
        this.cdRef.detectChanges();
      });
  }

  private getData(): void {
    this.store
      .select(InterviewTemplateSelectors.entities({ scope: InterviewTemplateScope.Interview }))
      .pipe(filterBoolean, takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(templates => {
        this.setState({
          ...this.state,
          templates: this.mapTemplates(templates),
        });
      });
  }
}
